import type { FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson';
import { DateTime } from 'luxon';
import type { MergeableAgencyField } from '~/common/mergeableAgencyFields';
import type {
  Agency,
  AgencyContact,
  AgencyLink,
  CivilGridFile,
  CivilSearchMessage,
  CivilSearchStoredFileType,
  CivilSearchTier,
  ContactMethod,
  DataRequestInvoice,
  DataRequestNote,
  DataRequestV2,
  FileDownload,
  OrganizationDetail,
  Project,
  RequestUpdate,
} from '~/common/types';
import { getAxiosClient } from './common';

const axios = getAxiosClient();

function getDateTime(date: Date | DateTime) {
  return DateTime.fromISO(date.toString());
}

function transformDataRequest(request: DataRequestV2): DataRequestV2 {
  return {
    ...request,
    createdAt: getDateTime(request.createdAt),
    updatedAt: getDateTime(request.updatedAt),
    ...(request.firstContactedAt && {
      firstContactedAt: getDateTime(request.firstContactedAt),
    }),
    ...(request.secondContactedAt && {
      secondContactedAt: getDateTime(request.secondContactedAt),
    }),
    ...(request.thirdContactedAt && {
      thirdContactedAt: getDateTime(request.thirdContactedAt),
    }),
    ...(request.fourthContactedAt && {
      fourthContactedAt: getDateTime(request.fourthContactedAt),
    }),
    ...(request.resolutionDate && {
      resolutionDate: getDateTime(request.resolutionDate),
    }),
    ...(request.calledDate && {
      calledDate: getDateTime(request.calledDate),
    }),
    ...(request.nextFollowUpAt && {
      nextFollowUpAt: getDateTime(request.nextFollowUpAt),
    }),
    invoices: request.invoices.map((i: any) => ({
      ...i,
      requestFulfilledAt: i.requestFulfilledAt ? DateTime.fromISO(i.requestFulfilledAt) : undefined,
      paymentToProviderAt: i.paymentToProviderAt ? DateTime.fromISO(i.paymentToProviderAt) : undefined,
      customerInvoicedAt: i.customerInvoicedAt ? DateTime.fromISO(i.customerInvoicedAt) : undefined,
    })),
  };
}

function transformAgency(agency: Agency): Agency {
  return {
    ...agency,
    createdAt: getDateTime(agency.createdAt),
    updatedAt: getDateTime(agency.updatedAt),
    ...(agency.sourceDate && { sourceDate: getDateTime(agency.sourceDate) }),
    ...(agency.lastRefresh && { lastRefresh: getDateTime(agency.lastRefresh) }),
    digitization: agency.digitization?.map((d) => ({
      ...d,
      createdAt: getDateTime(d.createdAt),
      updatedAt: getDateTime(d.updatedAt),
    })),
    ...(agency.logo?.uploadedAt && {
      logo: {
        ...agency.logo,
        uploadedAt: getDateTime(agency.logo.uploadedAt),
      },
    }),
  };
}

export async function getAgencies(): Promise<Agency[]> {
  const { data } = await axios.get<Agency[]>('/v2/civilsearch/agencies');
  return data.map((a) => transformAgency(a));
}

interface AgencyFilters {
  [key: string]: any;
}

interface ListFilteredAgenciesReturn {
  agencies: Agency[];
  search: AgencyFilters;
  totalDocuments: number;
}

export async function listFilteredAgencies({
  search,
}: {
  search: { [key: string]: any };
}): Promise<{ agencies: Agency[]; search: any; totalDocuments: number }> {
  const { data } = await axios.get<ListFilteredAgenciesReturn>(
    `/v2/civilsearch/listFilteredAgencies?search=${encodeURIComponent(JSON.stringify(search))}`,
  );

  return {
    ...data,
    agencies: data.agencies.map(transformAgency),
  };
}

export async function getAgency(
  agencyId: string,
): Promise<{ agency: Agency; contacts: AgencyContact[]; subGeometries: { id: string; name: string | null }[] }> {
  const { data } = await axios.get(`/v2/civilsearch/agencies/${agencyId}`);

  return {
    ...data,
    agency: transformAgency(data.agency),
    subGeometries: data.subGeometries ?? [],
  };
}

// Frozen so an accidental mutation by a downstream caller surfaces as a runtime
// error at the mutation site instead of silently corrupting later calls that
// share this singleton via the `projectBoundaries ?? EMPTY_FEATURE_COLLECTION`
// fallback below.
const EMPTY_FEATURE_COLLECTION: FeatureCollection = Object.freeze({
  type: 'FeatureCollection',
  features: Object.freeze([]) as never,
}) as FeatureCollection;

export type AgencyDiscoverySource = 'sharableRoutes' | 'networkIntelligence';

export type AgencyGeometriesResponse = FeatureCollection & {
  total: number;
  returned: number;
  projectBoundaries: FeatureCollection;
  fiberLitBuildings: FeatureCollection;
  discoverySources: AgencyDiscoverySource[];
};

export async function getAgencyGeometries(agencyId: string): Promise<AgencyGeometriesResponse> {
  const { data } = await axios.get<
    FeatureCollection & {
      total?: number;
      returned?: number;
      projectBoundaries?: FeatureCollection;
      fiberLitBuildings?: FeatureCollection;
      discoverySources?: AgencyDiscoverySource[];
    }
  >(`/v2/civilsearch/agencies/${agencyId}/geometries`);
  // Older API deploys may return a bare FeatureCollection without total/returned/projectBoundaries
  // foreign members; default each so the UI renders without spurious truncation or boundary layers.
  const featureCount = data.features?.length ?? 0;
  return {
    ...data,
    total: data.total ?? featureCount,
    returned: data.returned ?? featureCount,
    projectBoundaries: data.projectBoundaries ?? EMPTY_FEATURE_COLLECTION,
    fiberLitBuildings: data.fiberLitBuildings ?? EMPTY_FEATURE_COLLECTION,
    discoverySources: data.discoverySources ?? [],
  };
}

export async function updateAgency(agencyId: string, properties: { [key: string]: any }): Promise<Agency> {
  const { data } = await axios({
    url: `/v2/civilsearch/agencies/${agencyId}`,
    method: 'PUT',
    data: { properties },
  });

  return data;
}

export async function addAgencyLink(
  agencyId: string,
  link: Pick<AgencyLink, 'title' | 'description' | 'url' | 'type' | 'isPrivate'> & {
    agencySubGeometryId?: string | null;
  },
): Promise<AgencyLink> {
  const { data } = await axios({
    url: `/v2/civilsearch/agencies/${agencyId}/agencyLink`,
    method: 'POST',
    data: { link },
  });

  return data;
}

export async function editAgencyLink(agencyId: string, linkId: string, link: Partial<AgencyLink>): Promise<Agency> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/civilsearch/agencies/${agencyId}/agencyLink/${linkId}`,
    data: { link },
  });

  return data;
}

export async function deleteAgencyLink(agencyId: string, linkId: string): Promise<Agency> {
  const { data } = await axios.delete(`/v2/civilsearch/agencies/${agencyId}/agencyLink/${linkId}`);

  return data;
}

export async function addAgencyLogo(agencyId: string, file: any): Promise<Agency> {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/civilsearch/agencies/${agencyId}/logo`,
    data: file,
  });

  return transformAgency(data);
}

export async function newAgencyContact(properties: { [key: string]: any }): Promise<AgencyContact> {
  const { data } = await axios({
    url: `/v2/civilsearch/agencyContacts`,
    method: 'POST',
    data: { properties },
  });

  return data;
}

export async function updateAgencyContact(
  contactId: string,
  properties: { [key: string]: any },
): Promise<AgencyContact> {
  const { data } = await axios({
    url: `/v2/civilsearch/agencyContacts/${contactId}`,
    method: 'PUT',
    data: { properties },
  });

  return data;
}

export async function getDataRequests(): Promise<DataRequestV2[]> {
  const { data } = await axios.get<DataRequestV2[]>('/v2/civilsearch/dataRequests');

  return data.map((r) => transformDataRequest(r));
}

interface ListFilteredDataRequestsReturn {
  requests: DataRequestV2[];
  search: { [key: string]: any };
  totalDocuments: number;
}

export async function listFilteredDataRequests({ search }: { search: { [key: string]: any } }) {
  const { data } = await axios.get<ListFilteredDataRequestsReturn>(
    `/v2/civilsearch/listFilteredDataRequests?search=${encodeURIComponent(JSON.stringify(search))}`,
    {
      timeout: 120_000, // 2 minutes
    },
  );

  return {
    ...data,
    requests: data.requests.map(transformDataRequest),
  };
}

export async function getDataRequestsForProject(projectId: string): Promise<DataRequestV2[]> {
  const { data } = await axios.get<DataRequestV2[]>(`/v2/civilsearch/dataRequests?projectId=${projectId}`);

  return data.map((r) => transformDataRequest(r));
}

export async function getDataRequest<T = DataRequestV2>(requestId: string): Promise<T> {
  const { data: dataRequest } = await axios.get(`/v2/civilsearch/dataRequests/${requestId}`);
  return transformDataRequest(dataRequest) as T;
}

export async function updateDataRequest(requestId: string, properties: { [key: string]: any }): Promise<DataRequestV2> {
  const { data } = await axios({
    url: `/v2/civilsearch/dataRequests/${requestId}`,
    method: 'PUT',
    data: { properties },
  });

  return transformDataRequest(data);
}

export async function addDataRequestUpdate(requestId: string, update: Partial<RequestUpdate>): Promise<DataRequestV2> {
  const { data } = await axios({
    url: `/v2/civilsearch/dataRequests/${requestId}/updates`,
    method: 'POST',
    data: { update },
  });

  return transformDataRequest(data);
}

export async function deleteDataRequestUpdate(requestId: string, updateId: string): Promise<DataRequestV2> {
  const { data } = await axios.delete(`/v2/civilsearch/dataRequests/${requestId}/updates/${updateId}`);

  return transformDataRequest(data);
}

export async function addDataRequestNote(requestId: string, note: string): Promise<DataRequestNote> {
  const { data } = await axios({
    url: `/v2/civilsearch/dataRequests/${requestId}/notes`,
    method: 'POST',
    data: { note: { note } },
  });
  return data;
}

export async function updateDataRequestNote(requestId: string, noteId: string, note: string): Promise<DataRequestNote> {
  const { data } = await axios({
    url: `/v2/civilsearch/dataRequests/${requestId}/notes/${noteId}`,
    method: 'PUT',
    data: { note: { note } },
  });
  return data;
}

export async function deleteDataRequestNote(requestId: string, noteId: string): Promise<void> {
  await axios.delete(`/v2/civilsearch/dataRequests/${requestId}/notes/${noteId}`);
}

export async function addDataRequestInvoice(requestId: string, invoice: Partial<DataRequestInvoice>): Promise<unknown> {
  const { data } = await axios({
    url: `/v2/civilsearch/dataRequests/${requestId}/invoices`,
    method: 'POST',
    data: { invoice },
  });

  return data;
}

export async function updateDataRequestInvoice(
  requestId: string,
  invoiceId: string,
  invoice: Partial<DataRequestInvoice>,
): Promise<unknown> {
  const { data } = await axios({
    url: `/v2/civilsearch/dataRequests/${requestId}/invoices/${invoiceId}`,
    method: 'PUT',
    data: invoice,
  });

  return data;
}

export async function getSupportingFiles(projectId: string): Promise<CivilGridFile[]> {
  const { data } = await axios.get(`/v2/civilsearch/files?projectId=${projectId}`);
  return data;
}

/**
 * A short-lived signed attachment URL for a CivilSearch file in the given project, under admin
 * authorization; rejects without a request when no project is loaded.
 */
export async function getCivilSearchFileDownloadUrl(
  fileType: CivilSearchStoredFileType,
  fileId: string,
  projectId: string | undefined,
): Promise<FileDownload> {
  if (!projectId) throw new Error('No project loaded');
  const { data } = await axios.get<FileDownload>(`/v2/civilsearch/files/${fileType}/${fileId}/downloadUrl`, {
    params: { projectId },
  });
  return data;
}

export async function addSingleFile(projectId: string, file: FormData): Promise<CivilGridFile> {
  const { data } = await axios.post(`/v2/civilsearch/supportingFiles?projectId=${projectId}`, file);

  return data;
}

export async function deleteSingleFile(projectId: string, fileId: string): Promise<{ success?: true }> {
  const { data } = await axios.delete(`/v2/civilsearch/supportingFiles/${fileId}?projectId=${projectId}`);
  return data;
}

export async function addSingleRequestFile(
  projectId: string,
  requestId: string,
  file: FormData,
): Promise<CivilGridFile> {
  const { data } = await axios.post(
    `/v2/civilsearch/dataRequestFiles?projectId=${projectId}&requestId=${requestId}`,
    file,
  );

  return data;
}

export async function deleteSingleRequestFile(projectId: string, fileId: string): Promise<{ success?: true }> {
  const { data } = await axios.delete(`/v2/civilsearch/dataRequestFiles/${fileId}?projectId=${projectId}`);
  return data;
}

export async function updateRequestFileReferences(
  projectId: string,
  fileId: string,
  requestIdsSelection: string[],
): Promise<{ success?: true }> {
  const { data } = await axios({
    method: 'PUT',
    url: `/v2/civilsearch/files/${fileId}/updateRequestFileReferences?projectId=${projectId}`,
    data: { requestIdsSelection },
  });

  return data;
}

export async function getCivilSearchMessages(projectId: string): Promise<CivilSearchMessage[]> {
  const { data } = await axios.get(`/v2/civilsearch/civilSearchMessages?projectId=${projectId}`);

  return data;
}

export async function addCivilSearchMessage(
  projectId: string,
  message: { [key: string]: any },
): Promise<CivilSearchMessage> {
  const { data } = await axios({
    url: `/v2/civilsearch/civilSearchMessages?projectId=${projectId}`,
    method: 'POST',
    data: { message },
  });

  return data;
}

export async function getProject<T = Project>(projectId: string, includeRequests = false): Promise<T> {
  const { data } = await axios.get(`/v2/civilsearch/projects/${projectId}?includeRequests=${includeRequests}`);
  return data;
}

export async function searchProjects<T = Project>({
  id,
  projectId,
  name,
}: {
  id?: string;
  projectId?: string;
  name?: string;
}): Promise<T[]> {
  const { data } = await axios.get(`/v2/civilsearch/projects/search?id=${id}&projectId=${projectId}&name=${name}`);
  return data;
}

export async function searchOrganizations<T = OrganizationDetail>({
  id,
  name,
}: {
  id?: string;
  name?: string;
}): Promise<T[]> {
  const { data } = await axios.get(`/v2/civilsearch/organizations/search?id=${id}&name=${name}`);

  return data;
}

/** Create a Missive draft; the per-user PAT is sent in the `X-Missive-Token` header. */
export async function createMissiveEmailDraft(
  requestId: string,
  token: string,
): Promise<{ conversationId: string; webUrl: string }> {
  const { data } = await axios.post(`/v2/civilsearch/dataRequests/${requestId}/createMissiveEmailDraft`, undefined, {
    headers: { 'X-Missive-Token': token },
  });
  return data;
}

export async function addDigitizationAccess(
  agencyId: string,
  projectIds: string[],
  organizationIds: string[],
): Promise<Agency> {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/civilsearch/agencies/${agencyId}/digitization`,
    data: { projectIds, organizationIds },
  });

  return transformAgency(data);
}

export async function removeDigitizationAccess(agencyId: string, digitizationId: string): Promise<Agency> {
  const { data } = await axios.delete(`/v2/civilsearch/agencies/${agencyId}/digitization/${digitizationId}`);

  return transformAgency(data);
}

export async function listFilteredProjects({
  search,
}: {
  search: { [key: string]: any };
}): Promise<{ projects: Project[]; search: any; totalDocuments: number }> {
  const { data } = await axios.get<{
    projects: Project[];
    search: any;
    totalDocuments: number;
  }>(`/v2/civilsearch/listFilteredProjects?search=${encodeURIComponent(JSON.stringify(search))}`);
  return data;
}

interface UpdateRequestAssignmentsResponse {
  success: boolean;
  assignedTo: {
    id: string;
    email: string;
    name: string;
  };
}

export async function updateRequestAssignmentsByProjectId(
  projectId: string,
  assignedTo: string,
  civilSearchTier?: CivilSearchTier,
): Promise<UpdateRequestAssignmentsResponse> {
  const { data } = await axios.put(`/v2/civilsearch/projects/${projectId}/updateRequestAssignments`, {
    assignedTo,
    civilSearchTier,
  });
  return data;
}

export async function getAIProjectDescription(projectId: string, shouldForceNew: boolean = false) {
  const { data } = await axios.get(`/v2/civilsearch/projects/${projectId}/getAiProjectDescription`, {
    params: {
      shouldForceNew,
    },
  });
  return data;
}

export interface UtilityCheck {
  id: string;
  projectId: string;
  createdById: string;
  createdByName: string | null;
  assetType: string;
  notes: string;
  state: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectUtilityCheck {
  organizationName: string;
  organizationId: string;
  projectName: string;
  projectId: string;
  projectShortId: string;
  submittedDate: string;
  totalRequests: number;
  completedRequests: number;
  completionPercentage: number;
  completedUtilityChecks: number;
  majorityAssigneeId: string | null;
  majorityAssigneeName: string | null;
  majorityAssigneeEmail: string | null;
  utilityChecks: UtilityCheck[];
}

interface ListProjectUtilityChecksReturn {
  projects: ProjectUtilityCheck[];
  search: { [key: string]: any };
  totalDocuments: number;
}

export async function listProjectUtilityChecks({
  search,
}: {
  search: { [key: string]: any };
}): Promise<ListProjectUtilityChecksReturn> {
  const { data } = await axios.get<ListProjectUtilityChecksReturn>(
    `/v2/civilsearch/projectUtilityChecks?search=${encodeURIComponent(JSON.stringify(search))}`,
  );
  return data;
}

// Civil Searchers API Functions

export interface CivilSearcherResponse {
  id: string;
  userId: string;
  email: string;
  name: string;
  tiers: CivilSearchTier[];
  title: string;
  phone: string;
  leadId: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CivilSearcherUpdate {
  userId: string;
  tiers: CivilSearchTier[];
  title: string;
  phone: string;
  leadId: string | null;
}

export interface UpdateCivilSearchersRequest {
  searchers: CivilSearcherUpdate[];
}

export async function getCivilSearchers(): Promise<CivilSearcherResponse[]> {
  try {
    const { data } = await axios.get<CivilSearcherResponse[]>('/v2/civilsearch/civilSearchers');
    return data;
  } catch (error) {
    console.error('Failed to fetch civil searchers:', error);
    throw error;
  }
}

export type BulkUpdateSelection =
  | { mode: 'partial'; selectedIds: string[] }
  | { mode: 'all'; excludedIds: string[]; search: { [key: string]: any } };

export type BulkUpdateDataRequestsBody = {
  selection: BulkUpdateSelection;
  updates: Record<string, unknown>;
};

export async function bulkUpdateDataRequests(body: BulkUpdateDataRequestsBody): Promise<{ updatedCount: number }> {
  const { data } = await axios.put<{ updatedCount: number }>('/v2/civilsearch/dataRequests/bulkUpdate', body);
  return data;
}

export type BulkCreateDataRequestNotesBody = {
  selection: BulkUpdateSelection;
  note: string;
};

export async function bulkCreateDataRequestNotes(
  body: BulkCreateDataRequestNotesBody,
): Promise<{ createdCount: number }> {
  const { data } = await axios.post<{ createdCount: number }>('/v2/civilsearch/dataRequests/bulkNotes', body);
  return data;
}

export async function updateCivilSearchers(searchers: CivilSearcherUpdate[]): Promise<void> {
  try {
    const requestData: UpdateCivilSearchersRequest = { searchers };
    await axios.put('/v2/civilsearch/civilSearchers', requestData);
  } catch (error) {
    console.error('Failed to update civil searchers:', error);
    throw error;
  }
}

export interface CreateUtilityCheckInput {
  projectId: string;
  assetType: string;
  state?: string;
  notes?: string;
}

export async function createUtilityChecks(
  checks: CreateUtilityCheckInput[],
): Promise<{ utilityChecks: UtilityCheck[] }> {
  const { data } = await axios.post<{ utilityChecks: UtilityCheck[] }>('/v2/civilsearch/utilityChecks', { checks });
  return data;
}

/** The column type of each mergeable field, as the merge body sends it. */
export type MergeAgencyFieldValueTypes = {
  agencyName: string;
  type: string;
  isPublished: boolean;
  asbuiltsAvailable: boolean;
  providerId: string | null;
  pdfLink: string | null;
  siteLink: string | null;
  state: string;
  outreachEmail: string | null;
  outreachEmailName: string | null;
  publicNotes: string | null;
  doesNotRespond: boolean;
  chargesMapFees: boolean;
  requiresLetterhead: boolean;
  requiresNda: boolean;
  hideResponseEstimate: boolean;
  averageMapFeePerPage: number;
  mapFeeNote: string;
  preferredContactMethod: ContactMethod;
  prrUrl: string | null;
  portalContactFormUrl: string | null;
  aiContactResearch: string | null;
  logo: { name: string; path: string; type: string; extension: string; notes: string; uploadedAt?: string } | null;
  restrictions: { urban: number; rural: number } | null;
  civilSearchTier: CivilSearchTier;
  defaultCivilSearchAssigneeId: string | null;
  firstFollowUpDuration: number;
  secondFollowUpDuration: number;
  thirdFollowUpDuration: number;
  fourthFollowUpDuration: number;
  timeoutDuration: number;
};

/** Literal values for the new merged record's mergeable columns. */
export type MergeAgencyValues = Partial<Pick<MergeAgencyFieldValueTypes, MergeableAgencyField>>;

/** Mirrors the v2 body of `POST /v2/civilsearch/agencies/merge`: 2 to 10 distinct agency ids. */
export type MergeAgenciesRequest = {
  agencyIds: string[];
  /** A missing field takes the first non-null value across the inputs in `agencyIds` order. */
  values?: MergeAgencyValues;
};

export type MergeAgenciesResponse = { mergedAgencyId: string };

/**
 * The v1 merge body, accepted by the API for one release.
 * @deprecated Use {@link MergeAgenciesRequest}.
 */
export type MergeAgenciesParams = {
  primaryId: string;
  secondaryIds: string[];
  mappings?: {
    sourceFields: string[];
    targetFields: string[];
  };
};

export type MergeAgenciesV1Response = MergeAgenciesResponse & { mergedCount: number };

/** Merges the agencies into a new merged record that parents each of them. */
export async function mergeAgencies(params: MergeAgenciesRequest): Promise<MergeAgenciesResponse>;
/** @deprecated The v1 body; see {@link MergeAgenciesParams}. */
export async function mergeAgencies(params: MergeAgenciesParams): Promise<MergeAgenciesV1Response>;
export async function mergeAgencies(
  params: MergeAgenciesRequest | MergeAgenciesParams,
): Promise<MergeAgenciesResponse | MergeAgenciesV1Response> {
  const { data } = await axios.post<MergeAgenciesResponse | MergeAgenciesV1Response>(
    '/v2/civilsearch/agencies/merge',
    params,
  );
  return data;
}

// Mirrors `GetMergeHistoryResponse` / `MergeHistoryEntry` exported from
// packages/api/src/controllers/civilsearch/mergeAgencies.ts. `inbound`: `agencyId` was merged
// into the agency being viewed; `outbound`: the agency being viewed was merged into `agencyId`.
export type MergeHistoryEntry = {
  id: string;
  agencyId: string;
  agencyName: string | null;
  mergedAt: string | null;
  mergedBy: { id: string; name: string | null; email: string } | null;
  direction: 'inbound' | 'outbound';
};

export type GetMergeHistoryResponse = {
  history: MergeHistoryEntry[];
};

export async function getMergeHistory(agencyId: string): Promise<GetMergeHistoryResponse> {
  const { data } = await axios.get<GetMergeHistoryResponse>(`/v2/civilsearch/agencies/${agencyId}/mergeHistory`);
  return data;
}

export type MergeCountTriple = { total: number; willMove: number; willStayOnChild: number };

// One entry per input in request order, each counted against the inputs before it.
export type MergeAuditPreviewEntry = {
  agencyId: string;
  repointed: {
    dataRequestsV2: number;
    projectAgencies: { total: number; willMove: number; alreadyLinked: number; willStay: number };
    projectUtilityNotes: MergeCountTriple;
    agencyContacts: MergeCountTriple;
    agencyLinks: MergeCountTriple;
    agencyNotes: number;
    agencyDigitization: MergeCountTriple;
    intelligenceSummaryUndigitizedAgencies: MergeCountTriple;
  };
  stayingBehind: {
    drfRequests: number;
  };
  blockers: {
    overlappingSubGeometries: Array<{
      source: { id: string; agencyId: string; name: string | null };
      target: { id: string; agencyId: string; name: string | null };
    }>;
  };
};

export type MergeAuditPreviewResponse = {
  previews: MergeAuditPreviewEntry[];
};

/**
 * Returns counts of records that merging `agencyIds` would repoint or leave behind, one entry per
 * input in list order. Read-only; computed on demand.
 */
export async function getMergeAuditPreview(agencyIds: string[]): Promise<MergeAuditPreviewResponse> {
  const { data } = await axios.post<MergeAuditPreviewResponse>('/v2/civilsearch/agencies/merge/preview', {
    agencyIds,
  });
  return data;
}

export type AgencySubGeometryContact = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  title: string;
};

export type AgencySubGeometryLink = {
  id: string;
  type: string;
  title: string;
  url: string;
};

export type AgencySubGeometryNote = {
  id: string;
  notes: string;
  updatedAt: string;
};

export type AgencySubGeometry = {
  id: string;
  agencyId: string;
  name: string | null;
  status: string;
  geometry: Geometry;
  contacts: AgencySubGeometryContact[];
  links: AgencySubGeometryLink[];
  notes: AgencySubGeometryNote[];
};

export type AgencySubGeometryMemberContact = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  title: string;
  agencySubGeometryId: string | null;
};

export type AgencySubGeometryMemberLink = {
  id: string;
  type: string;
  title: string;
  url: string;
  agencySubGeometryId: string | null;
};

export type AgencySubGeometriesResponse = {
  subGeometries: AgencySubGeometry[];
  contacts: AgencySubGeometryMemberContact[];
  links: AgencySubGeometryMemberLink[];
};

export async function getAgencySubGeometries(agencyId: string): Promise<AgencySubGeometriesResponse> {
  const { data } = await axios.get<AgencySubGeometriesResponse>(`/v2/civilsearch/agencies/${agencyId}/subGeometries`);
  return { subGeometries: data.subGeometries ?? [], contacts: data.contacts ?? [], links: data.links ?? [] };
}

export async function updateAgencySubGeometryNote(
  agencyId: string,
  subGeometryId: string,
  notes: string,
): Promise<{ ok: true }> {
  const { data } = await axios.put<{ ok: true }>(
    `/v2/civilsearch/agencies/${agencyId}/subGeometries/${subGeometryId}/notes`,
    { notes },
  );
  return data;
}

export type CreatedAgencySubGeometry = {
  id: string;
  agencyId: string;
  name: string | null;
  status: string;
  geometry: Geometry;
};

// Returned by the API when the saved sub-geometry falls (partly) outside the agency's
// service area — a non-blocking warning (CG-473). Overlap, by contrast, is blocked (409).
export type SubGeometryWarning = {
  code: 'containment';
  containedFraction: number;
  threshold: number;
};

export async function createAgencySubGeometry(
  agencyId: string,
  body: { name: string; geometry: Polygon | MultiPolygon },
): Promise<{ subGeometry: CreatedAgencySubGeometry; warning?: SubGeometryWarning }> {
  const { data } = await axios.post<{ subGeometry: CreatedAgencySubGeometry; warning?: SubGeometryWarning }>(
    `/v2/civilsearch/agencies/${agencyId}/subGeometries`,
    body,
  );
  return data;
}

export async function updateAgencySubGeometry(
  agencyId: string,
  subGeometryId: string,
  body: { name: string; geometry: Polygon | MultiPolygon },
): Promise<{ subGeometry: CreatedAgencySubGeometry; warning?: SubGeometryWarning }> {
  const { data } = await axios.put<{ subGeometry: CreatedAgencySubGeometry; warning?: SubGeometryWarning }>(
    `/v2/civilsearch/agencies/${agencyId}/subGeometries/${subGeometryId}`,
    body,
  );
  return data;
}

// CG-480: archive (soft-delete) a sub-geometry. The server flips status -> archived and
// clears the scope FK on dependent contacts/links/notes; dataRequestsV2 rows are kept.
export async function archiveAgencySubGeometry(agencyId: string, subGeometryId: string): Promise<{ ok: true }> {
  const { data } = await axios.delete<{ ok: true }>(
    `/v2/civilsearch/agencies/${agencyId}/subGeometries/${subGeometryId}`,
  );
  return data;
}
